import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import EditorialPageHeader from "../components/EditorialPageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import "./operationalMigration.css";
import AdminLayout from "../components/AdminLayout";
import EmbeddedAdminLayout from "../components/EmbeddedAdminLayout";
import { 
  UserPlus, Users, Mail, Phone, Shield, CheckCircle, 
  XCircle, Clock, AlertCircle, MoreVertical, Eye,
  Send, Copy, Loader2, Search, Filter, ChevronDown,
  FileCheck, RefreshCw, CheckSquare, ExternalLink,
  ShieldCheck, ShieldAlert, ShieldX, ShieldQuestion
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const AdminCoaches = ({ embedded = false }) => {
  const PageLayout = embedded ? EmbeddedAdminLayout : AdminLayout;
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [coaches, setCoaches] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [teams, setTeams] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Background checks state
  const [backgroundChecks, setBackgroundChecks] = useState([]);
  const [checksLoading, setChecksLoading] = useState(false);
  const [checksError, setChecksError] = useState(false);
  const [bgCheckSummary, setBgCheckSummary] = useState({ total: 0, pending: 0, clear: 0, consider: 0, failed: 0 });
  const [bgCheckFilter, setBgCheckFilter] = useState("all");
  const [selectedChecks, setSelectedChecks] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Invite dialog
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
    org_id: "",
    team_ids: [],
    message: ""
  });
  const [sending, setSending] = useState(false);
  
  // Coach detail dialog
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, [user?.org_id, token]);

  useEffect(() => {
    if (activeTab === "background_checks") {
      fetchBackgroundChecks();
    }
  }, [activeTab, bgCheckFilter]);

  const fetchData = async () => {
    setLoadError(false);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [coachesRes, invitesRes, orgsRes] = await Promise.all([
        axios.get(`${API}/coaches`, { headers }),
        axios.get(`${API}/coaches/pending-invites`, { headers }),
        axios.get(`${API}/organizations`, { headers })
      ]);
      
      setCoaches(coachesRes.data || []);
      setPendingInvites(invitesRes.data || []);
      setOrganizations(orgsRes.data || []);
      
      // Set default org for invite form
      if (orgsRes.data?.length > 0) {
        setInviteForm(prev => ({ ...prev, org_id: orgsRes.data[0].org_id }));
      }
    } catch (error) {
      setLoadError(true);
      toast.error("Failed to load coaches");
    } finally {
      setLoading(false);
    }
  };

  const fetchBackgroundChecks = async () => {
    setChecksLoading(true);
    setChecksError(false);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = bgCheckFilter !== "all" ? `?status=${bgCheckFilter}` : "";
      const response = await axios.get(`${API}/coaches/background-checks/all${params}`, { headers });
      
      setBackgroundChecks(response.data.checks || []);
      setBgCheckSummary(response.data.summary || { total: 0, pending: 0, clear: 0, consider: 0, failed: 0 });
    } catch (error) {
      console.error("Failed to load background checks:", error);
      setChecksError(true);
    } finally {
      setChecksLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteForm.email || !inviteForm.first_name || !inviteForm.last_name || !inviteForm.org_id) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setSending(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/coaches/invite`, inviteForm, { headers });
      
      toast.success("Coach invitation sent!");
      
      // Copy invite URL to clipboard
      const inviteUrl = `${window.location.origin}/coach/register?token=${response.data.invite_url.split('token=')[1]}`;
      await navigator.clipboard.writeText(inviteUrl);
      toast.info("Invite link copied to clipboard");
      
      setInviteDialogOpen(false);
      setInviteForm({
        email: "",
        first_name: "",
        last_name: "",
        phone: "",
        org_id: organizations[0]?.org_id || "",
        team_ids: [],
        message: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    } finally {
      setSending(false);
    }
  };

  const handleApprove = async (coachId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/coaches/${coachId}/approve`, {}, { headers });
      toast.success("Coach approved!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve coach");
    }
  };

  const handleReject = async (coachId) => {
    if (!window.confirm("Are you sure you want to reject this coach application?")) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/coaches/${coachId}/reject`, {}, { headers });
      toast.success("Coach rejected");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject coach");
    }
  };

  const handleSimulateBackgroundCheck = async (coachId, result) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/coaches/${coachId}/background-check/simulate?result=${result}`, {}, { headers });
      toast.success(`Background check simulated: ${result}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to simulate background check");
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/coaches/invite/${inviteId}`, { headers });
      toast.success("Invite cancelled");
      fetchData();
    } catch (error) {
      toast.error("Failed to cancel invite");
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedChecks.length === 0) {
      toast.error("Please select at least one background check");
      return;
    }
    
    const actionLabels = {
      approve_coaches: "approve coaches with clear background checks",
      mark_reviewed: "mark as reviewed",
      retry: "retry background checks"
    };
    
    if (!window.confirm(`Are you sure you want to ${actionLabels[action]} for ${selectedChecks.length} selected item(s)?`)) {
      return;
    }
    
    setBulkActionLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/coaches/background-checks/bulk-action`, {
        action,
        check_ids: selectedChecks
      }, { headers });
      
      toast.success(`Action completed: ${response.data.success} successful, ${response.data.failed} failed`);
      setSelectedChecks([]);
      fetchBackgroundChecks();
      fetchData();
    } catch (error) {
      toast.error("Failed to perform bulk action");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const toggleCheckSelection = (checkId) => {
    setSelectedChecks(prev => 
      prev.includes(checkId) 
        ? prev.filter(id => id !== checkId)
        : [...prev, checkId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedChecks.length === backgroundChecks.length) {
      setSelectedChecks([]);
    } else {
      setSelectedChecks(backgroundChecks.map(c => c.check_id));
    }
  };

  const getBgCheckStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "bg-amber-100 text-amber-700", icon: ShieldQuestion, label: "Pending" },
      clear: { color: "bg-emerald-100 text-emerald-700", icon: ShieldCheck, label: "Clear" },
      consider: { color: "bg-orange-100 text-orange-700", icon: ShieldAlert, label: "Consider" },
      failed: { color: "bg-red-100 text-red-700", icon: ShieldX, label: "Failed" }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      invited: { color: "bg-blue-100 text-blue-700", icon: Mail },
      registered: { color: "bg-slate-100 text-slate-700", icon: Users },
      background_check_pending: { color: "bg-amber-100 text-amber-700", icon: Shield },
      background_check_passed: { color: "bg-emerald-100 text-emerald-700", icon: Shield },
      background_check_failed: { color: "bg-red-100 text-red-700", icon: Shield },
      pending_approval: { color: "bg-amber-100 text-amber-700", icon: Clock },
      approved: { color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
      rejected: { color: "bg-red-100 text-red-700", icon: XCircle },
      suspended: { color: "bg-red-100 text-red-700", icon: AlertCircle }
    };
    
    const config = statusConfig[status] || { color: "bg-slate-100 text-slate-700", icon: Users };
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {status.replace(/_/g, ' ')}
      </span>
    );
  };

  const filteredCoaches = coaches.filter(coach => {
    const matchesSearch = searchQuery === "" || 
      coach.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      coach.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      coach.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesTab = activeTab === "all" || 
      (activeTab === "pending" && ["pending_approval", "background_check_pending"].includes(coach.status)) ||
      (activeTab === "approved" && coach.status === "approved") ||
      (activeTab === "rejected" && ["rejected", "background_check_failed"].includes(coach.status));
    
    return matchesSearch && matchesTab;
  });

  const pendingCount = coaches.filter(c => ["pending_approval", "background_check_pending"].includes(c.status)).length;

  if (loadError && !loading) {
    return (
      <PageLayout>
        <div className="operational-migration space-y-6">
          <EditorialPageHeader title="Coaches" />
          <div role="alert" className="migration-panel">
            <p>Failed to load coaches. Please try again.</p>
            <Button variant="outline" onClick={() => { setLoading(true); fetchData(); }}>Retry</Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (loading) {
    return (
      <PageLayout>
        <div role="status" className="operational-migration flex items-center justify-center gap-3 h-64">
          <span>Loading coaches…</span>
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="operational-migration coaches-workspace space-y-6">
        <EditorialPageHeader
          eyebrow="People"
          title="Coaches"
          description="Manage coach invitations and applications"
          headingId="coaches-title"
          aside={<Button onClick={() => setInviteDialogOpen(true)}><UserPlus className="w-4 h-4 mr-2" />Invite Coach</Button>}
        />

        {/* Pending Invites */}
        {pendingInvites.length > 0 && (
          <div className="migration-panel pending-invitations">
            <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Pending Invitations ({pendingInvites.length})
            </h3>
            <div className="space-y-2">
              {pendingInvites.map((invite) => (
                <div key={invite.invite_id} className="invitation-row">
                  <div>
                    <p className="font-medium text-slate-900">{invite.first_name} {invite.last_name}</p>
                    <p className="text-sm text-slate-500">{invite.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">
                      Sent {new Date(invite.created_at).toLocaleDateString()}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCancelInvite(invite.invite_id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs & Search */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="coach-toolbar">
          <TabsList aria-label="Coach views" className="migration-tabs">
            {[
              { id: "all", label: "All Coaches" },
              { id: "pending", label: "Pending Review", count: pendingCount },
              { id: "approved", label: "Approved" },
              { id: "rejected", label: "Rejected" },
              { id: "background_checks", label: "Background Checks", icon: Shield }
            ].map(tab => (
              <TabsTrigger key={tab.id} value={tab.id}>
                {tab.label}
                {tab.count > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
                    {tab.count}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
          
          <div className="relative coach-search">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              aria-label="Search coaches"
              placeholder="Search coaches..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-full"
            />
          </div>
        </div>

        <TabsContent value={activeTab}>
        {/* Background Checks Tab Content */}
        {activeTab === "background_checks" ? checksLoading ? (
          <div role="status" className="migration-panel">Loading background checks…</div>
        ) : checksError ? (
          <div role="alert" className="migration-panel">
            <p>Failed to load background checks. Please try again.</p>
            <Button variant="outline" onClick={fetchBackgroundChecks}>Retry background checks</Button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="check-summary">
              {[
                { label: "Total", value: bgCheckSummary.total, color: "bg-slate-100 text-slate-700", filter: "all" },
                { label: "Pending", value: bgCheckSummary.pending, color: "bg-amber-100 text-amber-700", filter: "pending" },
                { label: "Clear", value: bgCheckSummary.clear, color: "bg-emerald-100 text-emerald-700", filter: "clear" },
                { label: "Consider", value: bgCheckSummary.consider, color: "bg-orange-100 text-orange-700", filter: "consider" },
                { label: "Failed", value: bgCheckSummary.failed, color: "bg-red-100 text-red-700", filter: "failed" }
              ].map(stat => (
                <button
                  key={stat.filter}
                  onClick={() => setBgCheckFilter(stat.filter)}
                  aria-pressed={bgCheckFilter === stat.filter}
                  className="check-summary-filter"
                >
                  <p className="text-sm text-slate-500">{stat.label}</p>
                  <p className="text-lg font-semibold tabular-nums">{stat.value}</p>
                </button>
              ))}
            </div>

            {/* Bulk Actions */}
            {selectedChecks.length > 0 && (
              <div className="migration-panel flex items-center justify-between flex-wrap gap-4">
                <span className="font-medium">{selectedChecks.length} selected</span>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    onClick={() => handleBulkAction("approve_coaches")}
                    disabled={bulkActionLoading}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Approve Clear
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleBulkAction("mark_reviewed")}
                    disabled={bulkActionLoading}
                    variant="outline"
                  >
                    <CheckSquare className="w-4 h-4 mr-1" />
                    Mark Reviewed
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleBulkAction("retry")}
                    disabled={bulkActionLoading}
                    variant="outline"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Retry
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedChecks([])}
                    className="text-muted-foreground"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Background Checks Table */}
            <div className="migration-panel migration-table">
              {backgroundChecks.length === 0 ? (
                <div className="text-center py-12">
                  <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">No background checks found</h3>
                  <p className="text-slate-500">Background checks will appear here when coaches register</p>
                </div>
              ) : (
                <table aria-label={activeTab === "background_checks" ? "Background checks" : "Coaches"} className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3">
                        <Checkbox
                          aria-label="Select all background checks"
                          checked={selectedChecks.length === backgroundChecks.length && backgroundChecks.length > 0}
                          onCheckedChange={toggleSelectAll}
                        />
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Coach</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Coach Status</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Organization</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Created</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Completed</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {backgroundChecks.map((check) => (
                      <tr key={check.check_id} className="hover:bg-slate-50">
                        <td className="px-4 py-4">
                          <Checkbox
                            aria-label={`Select ${check.coach_name || "Unknown coach"}`}
                            checked={selectedChecks.includes(check.check_id)}
                            onCheckedChange={() => toggleCheckSelection(check.check_id)}
                          />
                        </td>
                        <td className="px-4 py-4">
                          <div>
                            <p className="font-medium text-slate-900">{check.coach_name || "Unknown"}</p>
                            <p className="text-sm text-slate-500">{check.coach_email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          {getBgCheckStatusBadge(check.status)}
                          {check.mocked && (
                            <span className="ml-2 text-xs text-slate-400">(Test)</span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          {check.coach_status && getStatusBadge(check.coach_status)}
                        </td>
                        <td className="px-4 py-4 text-sm text-slate-600">
                          {check.organization_name}
                        </td>
                        <td className="px-4 py-4 text-sm text-slate-500">
                          {check.created_at ? new Date(check.created_at).toLocaleDateString() : "-"}
                        </td>
                        <td className="px-4 py-4 text-sm text-slate-500">
                          {check.completed_at ? new Date(check.completed_at).toLocaleDateString() : "-"}
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {check.checkr_invitation_url && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(check.checkr_invitation_url, '_blank')}
                                title="View in Checkr"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            )}
                            {check.status === "clear" && check.coach_status !== "approved" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleApprove(check.coach_id)}
                                className="text-emerald-600 hover:text-emerald-700"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                            )}
                            {check.status === "failed" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleBulkAction("retry")}
                                className="text-slate-600"
                              >
                                <RefreshCw className="w-4 h-4 mr-1" />
                                Retry
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ) : (
        /* Coaches List */
        <div className="migration-panel migration-table">
          {filteredCoaches.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-1">No coaches found</h3>
              <p className="text-slate-500">Invite your first coach to get started</p>
            </div>
          ) : (
            <table aria-label={activeTab === "background_checks" ? "Background checks" : "Coaches"} className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Coach</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Contact</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Background</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredCoaches.map((coach) => (
                  <tr key={coach.coach_id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {coach.photo_url ? (
                          <img loading="lazy" src={coach.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 font-medium">
                            {coach.first_name?.[0]}{coach.last_name?.[0]}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-slate-900">{coach.first_name} {coach.last_name}</p>
                          <p className="text-sm text-slate-500">{coach.certifications?.length || 0} certifications</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-slate-900">{coach.email}</p>
                      <p className="text-sm text-slate-500">{coach.phone || "No phone"}</p>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(coach.status)}
                    </td>
                    <td className="px-6 py-4">
                      {coach.background_check_status ? (
                        <span className={`text-sm ${
                          coach.background_check_status === 'clear' ? 'text-emerald-600' :
                          coach.background_check_status === 'pending' ? 'text-amber-600' :
                          'text-red-600'
                        }`}>
                          {coach.background_check_status}
                        </span>
                      ) : (
                        <span className="text-sm text-slate-400">Not requested</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {coach.status === "pending_approval" && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleApprove(coach.coach_id)}
                              className="bg-primary hover:bg-primary/90 text-primary-foreground"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReject(coach.coach_id)}
                              className="text-destructive border-destructive/20 hover:bg-destructive/10"
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                        {coach.status === "background_check_pending" && user?.role === "super_admin" && (
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSimulateBackgroundCheck(coach.coach_id, "clear")}
                              className="text-emerald-600 text-xs"
                            >
                              Sim: Clear
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSimulateBackgroundCheck(coach.coach_id, "failed")}
                              className="text-red-600 text-xs"
                            >
                              Sim: Fail
                            </Button>
                          </div>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`View ${coach.first_name} ${coach.last_name}`}
                          onClick={() => {
                            setSelectedCoach(coach);
                            setDetailDialogOpen(true);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        )}
        </TabsContent>
        </Tabs>
      </div>

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="operational-migration sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite a Coach</DialogTitle>
            <DialogDescription>
              Send an invitation email to a coach to join your organization.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="coach-invite-first_name">First Name *</Label>
                <Input
                  id="coach-invite-first_name"
                  value={inviteForm.first_name}
                  onChange={(e) => setInviteForm(prev => ({ ...prev, first_name: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="coach-invite-last_name">Last Name *</Label>
                <Input
                  id="coach-invite-last_name"
                  value={inviteForm.last_name}
                  onChange={(e) => setInviteForm(prev => ({ ...prev, last_name: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="coach-invite-email">Email *</Label>
              <Input
                type="email"
                id="coach-invite-email"
                  value={inviteForm.email}
                onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="coach@example.com"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="coach-invite-phone">Phone</Label>
              <Input
                type="tel"
                id="coach-invite-phone"
                  value={inviteForm.phone}
                onChange={(e) => setInviteForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="(555) 123-4567"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="coach-invite-message">Personal Message (Optional)</Label>
              <Textarea
                id="coach-invite-message"
                  value={inviteForm.message}
                onChange={(e) => setInviteForm(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Add a personal note to the invitation..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleInvite} disabled={sending}>
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Coach Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Coach Details</DialogTitle>
            <DialogDescription>Contact information, status and coaching qualifications.</DialogDescription>
          </DialogHeader>
          
          {selectedCoach && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                {selectedCoach.photo_url ? (
                  <img loading="lazy" src={selectedCoach.photo_url} alt="" className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center text-xl font-medium text-slate-500">
                    {selectedCoach.first_name?.[0]}{selectedCoach.last_name?.[0]}
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-semibold">{selectedCoach.first_name} {selectedCoach.last_name}</h3>
                  <p className="text-slate-500">{selectedCoach.email}</p>
                  {getStatusBadge(selectedCoach.status)}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Phone</p>
                  <p className="font-medium">{selectedCoach.phone || "Not provided"}</p>
                </div>
                <div>
                  <p className="text-slate-500">Date of Birth</p>
                  <p className="font-medium">{selectedCoach.date_of_birth || "Not provided"}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-slate-500">Address</p>
                  <p className="font-medium">
                    {selectedCoach.address ? `${selectedCoach.address}, ${selectedCoach.city}, ${selectedCoach.state} ${selectedCoach.zip_code}` : "Not provided"}
                  </p>
                </div>
              </div>
              
              {selectedCoach.emergency_contact?.name && (
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Emergency Contact</p>
                  <p className="font-medium">{selectedCoach.emergency_contact.name}</p>
                  <p className="text-sm text-slate-500">{selectedCoach.emergency_contact.phone} ({selectedCoach.emergency_contact.relationship})</p>
                </div>
              )}
              
              {selectedCoach.certifications?.length > 0 && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">Certifications</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedCoach.certifications.map((cert, i) => (
                      <span key={i} className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                        {typeof cert === 'string' ? cert : (cert?.name || cert?.filename || 'Certificate')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedCoach.coaching_experience && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Coaching Experience</p>
                  <p className="text-sm">{selectedCoach.coaching_experience}</p>
                </div>
              )}
              
              {selectedCoach.bio && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Bio</p>
                  <p className="text-sm">{selectedCoach.bio}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default AdminCoaches;
